import { DateTime } from 'luxon';
import { LogRecordState, LogRecordType } from '@cf-shared/data-model/user-log';

// Which workout of the user's plan a given log entry is, and how sure the app is about it.
//
// A plan is a rotation: the user goes through Workout A, Workout B, Workout C and starts
// over. `planReference` is the position in that rotation (0 = A, 1 = B, ...). A scheduled
// workout the user has not trained yet carries no `planReference` at all — the app works it
// out by looking back at the previous workout and taking the next one in the rotation.
//
// This module holds only the decision. The looking-back itself is the caller's job, because
// it reads the backlog cache, and the cache cannot be loaded under plain node. Same split,
// and the same reason, as `sync-revision-decisions.ts`.

/** The facts about the previous workout that the rotation rule needs. */
export interface PreviousWorkoutFacts {
  /** which workout of the plan the previous one was */
  planReference: number;
  state: LogRecordState;
  /** the previous workout's own local date, as YYYY-MM-DD */
  dateISO: string;
}

/** How much the caller's look-back is worth. */
export enum LookBackCoverage {
  /** a previous workout was found, so nothing else matters */
  FoundPrevious = 'found-previous',
  /**
   * no previous workout was found, AND the caller can prove it looked back over every day
   * from the user's first log entry onwards. So there really is no earlier workout.
   */
  NoneAndProven = 'none-and-proven',
  /**
   * no previous workout was found, and the caller CANNOT prove it looked far enough back.
   * The in-memory list of log days it searched may have been emptied, or may never have
   * reached back to the user's first log entry.
   */
  NoneAndUnproven = 'none-and-unproven',
}

/** Where the answer came from, and therefore whether it may be written into a document. */
export enum PlanReferenceSource {
  /** already written on the document — nothing was worked out, so nothing can be wrong */
  StoredOnDocument = 'stored-on-document',
  /** worked out from the previous workout — trustworthy */
  DerivedFromPrevious = 'derived-from-previous',
  /** the user has no earlier workout at all, proven — trustworthy */
  FirstWorkoutInLog = 'first-workout-in-log',
  /** nothing was found and nothing was proven. The number is a guess: show it, never store it. */
  Unknown = 'unknown',
}

export interface PlanReferenceAnswer {
  /** always a usable number, so a screen can render something */
  planReference: number;
  source: PlanReferenceSource;
}

/**
 * What a look-back that found no previous workout is worth (#5640).
 *
 * @param searchedFromDay the earliest day the caller can vouch for having searched, on an
 *   unbroken run back from the workout, or undefined when it cannot vouch for any
 * @param earliestLogDay the day the user's log starts, or undefined when they have no log
 *   entries at all
 * @param earliestLogDayLookedUp whether anything has yet looked that up. Kept separate on
 *   purpose: "the user has nothing logged" proves this is their first workout, while "nobody
 *   looked" proves nothing, and one undefined cannot say both.
 * @param noSavedWorkoutBeforeDay a day before which a read of the user's whole log found no
 *   saved workout at all, or undefined when no such read ran or it found one. It closes the
 *   gap between the log's first day and a searched run that could not reach back that far:
 *   someone who logged meals for six months before their first workout (df-afa259234d1a).
 */
export function describeEmptyLookBack(
  searchedFromDay: string | undefined,
  earliestLogDay: string | undefined,
  earliestLogDayLookedUp: boolean,
  noSavedWorkoutBeforeDay?: string,
): LookBackCoverage {
  // nobody has looked up where this user's log starts, so nothing can be proven
  if (!earliestLogDayLookedUp) {
    return LookBackCoverage.NoneAndUnproven;
  }
  // the user has no log entries at all, so there is certainly no earlier workout
  if (earliestLogDay === undefined) {
    return LookBackCoverage.NoneAndProven;
  }
  // otherwise the searched run must reach back to the day their log starts
  if (searchedFromDay !== undefined && searchedFromDay <= earliestLogDay) {
    return LookBackCoverage.NoneAndProven;
  }
  // or the days the run did not reach must be proven to hold no saved workout. The run itself
  // must still be unbroken: the read only covers the days before it, never a hole inside it
  if (searchedFromDay !== undefined && noSavedWorkoutBeforeDay !== undefined && searchedFromDay <= noSavedWorkoutBeforeDay) {
    return LookBackCoverage.NoneAndProven;
  }
  return LookBackCoverage.NoneAndUnproven;
}

/**
 * Whether the day this device says the user's log starts can be trusted as the real one
 * (df-bf4f51790720). A new device first downloads only the last few months of the log and
 * reads its own copy from then on, while the older history arrives in the background. Until
 * it has, the local first log day is only where the download got to, so a look-back reaching
 * it would "prove" a returning user's workout is their first.
 *
 * @param backgroundSyncComplete whether this device has downloaded the user's older history
 */
export function mayTrustEarliestLogDay(backgroundSyncComplete: boolean): boolean {
  return backgroundSyncComplete;
}

/**
 * The UTC day the read for an earlier saved workout must reach, or undefined when the read may
 * not run at all (df-afa259234d1a). The read takes every log id before `log/<that day>`.
 *
 * @param searchedFromDay the first local day of the unbroken run the cache searched
 * @param backgroundSyncComplete whether this device has downloaded the user's older history.
 *   Before that it may hold an old day it fetched on demand without the days around it, so
 *   finding no workout there would prove nothing.
 */
export function earlierWorkoutReadEndUtcDay(searchedFromDay: string, backgroundSyncComplete: boolean): string | undefined {
  if (!backgroundSyncComplete) {
    return undefined;
  }
  // log ids carry UTC times, and a local day can begin up to 14 hours before or after the UTC
  // one. Reading up to two UTC days after the run's first day covers that day in every zone;
  // the overlap with days the run already searched only makes the read stricter
  return DateTime.fromISO(searchedFromDay, { zone: 'utc' }).plus({ days: 2 }).toISODate() ?? undefined;
}

/**
 * Whether a look-back that ended "unknown" should be run once more before it is reported:
 * yes when the backlog cache was emptied while it ran, and only once. Why, and the incident
 * behind it: design-docs/workout-plan-reference.md, the "Writing goes through" bullet.
 *
 * @param generationAtStart the cache's generation count when the look-back began
 * @param generationNow the count when it ended "unknown"; it moves only when cached days are dropped
 * @param alreadyRetried whether this look-back is already the second pass
 */
export function shouldRetryLookBackAfterCacheCleared(generationAtStart: number, generationNow: number, alreadyRetried: boolean): boolean {
  return !alreadyRetried && generationNow !== generationAtStart;
}

/** Whether any of these log ids (`log/<time_utc>-<type>`) is a saved workout. */
export function logIdsIncludeWorkout(ids: string[]): boolean {
  return ids.some(id => id.endsWith(`-${LogRecordType.Workout}`));
}

/** Whether an answer is solid enough to be written into the workout document. */
export function mayStampPlanReference(answer: PlanReferenceAnswer): boolean {
  return answer.source !== PlanReferenceSource.Unknown;
}

/**
 * Work out which workout of the plan this one is, given what the look-back found.
 *
 * @param previous the previous workout's facts, or null when none was found
 * @param coverage what the caller's look-back is worth (see LookBackCoverage)
 * @param workoutCount how many workouts the plan rotates through
 * @param todayISO today's local date as YYYY-MM-DD
 */
export function decideNextPlanReference(
  previous: PreviousWorkoutFacts | null,
  coverage: LookBackCoverage,
  workoutCount: number,
  todayISO: string,
): PlanReferenceAnswer {
  // a plan with no workouts has nothing to point at, and nothing worth storing
  if (workoutCount === 0) {
    return { planReference: 0, source: PlanReferenceSource.Unknown };
  }

  if (previous) {
    // the previous workout's day may since have been deleted from the plan (#6850), so every
    // answer below is wrapped back inside it
    // if the previous workout was skipped, the rotation does not advance — the user still
    // owes that workout, so this one is the same one again
    if (previous.state === LogRecordState.Skipped) {
      return { planReference: wrapPlanReference(previous.planReference, workoutCount), source: PlanReferenceSource.DerivedFromPrevious };
    }

    // if the previous workout was only scheduled (never started) AND is in the past, the
    // rotation does not advance either. This is what keeps a missed workout on offer up to
    // today; workouts further in the future resume ordinary A, B, A, B rotation.
    if (previous.state === LogRecordState.Scheduled && previous.dateISO < todayISO) {
      return { planReference: wrapPlanReference(previous.planReference, workoutCount), source: PlanReferenceSource.DerivedFromPrevious };
    }

    return {
      planReference: wrapPlanReference(previous.planReference + 1, workoutCount),
      source: PlanReferenceSource.DerivedFromPrevious,
    };
  }

  // No previous workout was found. Whether that means anything depends entirely on how far
  // the caller was able to look: only a look-back that reached the user's first log entry
  // proves there is no earlier workout. Anything else is a guess, and #5640 is what happens
  // when a guess gets written into a document.
  if (coverage === LookBackCoverage.NoneAndProven) {
    return { planReference: 0, source: PlanReferenceSource.FirstWorkoutInLog };
  }
  return { planReference: 0, source: PlanReferenceSource.Unknown };
}

/**
 * A plan reference brought back inside a plan of `workoutCount` workouts (#6850). A stored
 * reference can point past the end once days are deleted from the plan, and a damaged
 * document can hold a negative number or a fraction; both would index no workout at all.
 */
export function wrapPlanReference(planReference: number, workoutCount: number): number {
  const whole = Number.isFinite(planReference) ? Math.trunc(planReference) : 0;
  return ((whole % workoutCount) + workoutCount) % workoutCount;
}

/** What to do about a stored plan reference the plan may no longer have (#6850). */
export enum OutOfRangePlanReferenceRepair {
  /** the reference points at a workout the plan has, or there is none to check */
  InRange = 'in-range',
  /** out of range, and nothing was trained: forget it and let the rotation decide again */
  ResetUntrained = 'reset-untrained',
  /** out of range, but the user trained in it or it is finished: leave it, and report */
  LeaveStarted = 'leave-started',
}

/**
 * Whether a workout's stored plan reference still points at a workout of the plan, and what to
 * do when it does not (#6850).
 *
 * Edit Workouts can delete the day a workout was stamped with, and it never touches workouts
 * already in the log. A reference past the end builds no exercises at all, so the Training page
 * showed an empty day. A workout with nothing trained holds nothing the user entered, so it is
 * safe to forget its reference and build it again from the rotation. A workout with a logged set
 * is not: its sets are what the user did, and the exercise merge keeps them on screen anyway.
 *
 * @param storedPlanReference the plan reference written on the document, if any
 * @param workoutCount how many workouts the plan has now
 * @param state the workout's own state
 * @param exercises the workout's stored exercise list, read defensively because an older version
 *   or a damaged document may have left holes in it
 */
export function decideOutOfRangePlanReferenceRepair(
  storedPlanReference: number | undefined,
  workoutCount: number,
  state: LogRecordState | undefined,
  exercises: readonly ({ sets?: readonly ({ state?: LogRecordState } | undefined)[] } | undefined)[] | undefined,
): OutOfRangePlanReferenceRepair {
  if (storedPlanReference === undefined) return OutOfRangePlanReferenceRepair.InRange;
  // a plan with no workouts has its own report and rebuild offer (#4587)
  if (workoutCount === 0) return OutOfRangePlanReferenceRepair.InRange;
  if (Number.isInteger(storedPlanReference) && storedPlanReference >= 0 && storedPlanReference < workoutCount) {
    return OutOfRangePlanReferenceRepair.InRange;
  }

  // a finished workout is history, whatever the plan looks like today
  if (state === LogRecordState.Done || state === LogRecordState.Skipped) return OutOfRangePlanReferenceRepair.LeaveStarted;

  // a done or skipped set is something the user entered, so the workout must not be rebuilt
  const anySetLogged = (exercises ?? []).some(exercise => exercise?.sets?.some(
    set => set?.state === LogRecordState.Done || set?.state === LogRecordState.Skipped));
  return anySetLogged ? OutOfRangePlanReferenceRepair.LeaveStarted : OutOfRangePlanReferenceRepair.ResetUntrained;
}

/** What to write onto a workout document when it is stamped (#5640). */
export interface WorkoutStamp {
  /** the plan reference to write, or undefined to leave the document's own alone */
  planReference?: number;
  /** the English name to write, or undefined to leave the document's own alone */
  name?: string;
}

/**
 * What to stamp onto a workout at the moment its exercise list is built from the plan
 * (#5640). Answers an empty object when nothing should be written.
 *
 * This is the gate that keeps `planReference`, `name` and `exercises` one decision rather
 * than three. The exercises have just been built from `stampablePlanReference`, so this is
 * the one moment where the label and the exercise list provably agree; leaving the label to
 * the first save re-asks the question minutes later and can get a different answer, which is
 * the bug #5640 was raised for.
 *
 * It lives here rather than in `WorkoutLoaderService` so that a test can drive it. The
 * service needs the whole database stack to construct, so its only cover was a test that
 * read the service's own source as text and matched strings in it — which pins the wording
 * rather than the rule.
 *
 * @param storedPlanReference the plan reference already on the document, or undefined when
 *   it carries none. An answer already written is never second-guessed.
 * @param storedName the English name already on the document. A name the user or an earlier
 *   save put there is never overwritten.
 * @param stampablePlanReference the reference the caller's look-back settled on, or
 *   undefined when it could not tell. Undefined leaves the document unstamped, which
 *   displays exactly as an unstamped document always has, and the next save that CAN answer
 *   stamps it.
 * @param planWorkoutName the plan's own name for that workout — its letter, such as `'B'` —
 *   or undefined when the plan gives it none
 * @param mayStampAtThisMode whether this workout is one the user can train now. A workout
 *   further ahead may merely have been opened for a look, and its place in the rotation is
 *   not settled: skipping or rescheduling an earlier workout still moves it. The mode test
 *   itself stays with the caller, because which load modes count is the caller's contract.
 */
export function decideWorkoutStamp(
  storedPlanReference: number | undefined,
  storedName: string | undefined,
  stampablePlanReference: number | undefined,
  planWorkoutName: string | undefined,
  mayStampAtThisMode: boolean,
): WorkoutStamp {
  // a workout the user cannot train now has an unsettled place in the rotation, so freezing
  // one here would change how the rotation behaves
  if (!mayStampAtThisMode) return {};

  // already answered — nothing was worked out, so there is nothing here that can be wrong
  if (storedPlanReference !== undefined) return {};

  // the look-back could not settle it, and a guess must never be stored
  if (stampablePlanReference === undefined) return {};

  const stamp: WorkoutStamp = { planReference: stampablePlanReference };

  // the name comes from the reference just stamped, so the two cannot disagree. An empty
  // string when the plan names that workout nothing: the display form is derived from the
  // reference, so nothing is lost on screen.
  if (!storedName) {
    stamp.name = planWorkoutName ? 'Workout ' + planWorkoutName : '';
  }
  return stamp;
}

/**
 * Whether a workout's stored plan reference is contradicted by the exercises the user has
 * actually logged a set on, and if so which workout of the plan it should say instead
 * (#5640). Answers undefined when nothing should change.
 *
 * The question this answers is narrow on purpose. It says yes only when the logged sets
 * settle it outright: not one of them belongs to the workout the document claims, and
 * exactly one other workout of the plan holds every last one of them. A user who
 * deliberately changed workout has an exercise list that matches their label, so the first
 * condition saves them. A plan edit that moved one exercise to another day leaves the other
 * logged exercises still matching, so the first condition saves them too.
 *
 * @param storedPlanReference the plan reference currently written on the document
 * @param workedSubSlotIds the sub-slots the user has logged at least one finished set on
 * @param workoutSubSlotIds each plan workout's sub-slot ids, in plan order
 */
export function decidePlanReferenceHeal(
  storedPlanReference: number,
  workedSubSlotIds: readonly string[],
  workoutSubSlotIds: readonly (readonly string[])[],
): number | undefined {
  // nothing logged means nothing to argue from
  if (workedSubSlotIds.length === 0) return undefined;

  const claimed = workoutSubSlotIds[storedPlanReference];
  // a reference pointing outside the plan is a different problem, and guessing here would
  // hide it rather than fix it
  if (!claimed) return undefined;

  // if even one logged exercise belongs to the workout the document claims, the label is not
  // plainly wrong and nothing here is safe to change
  if (workedSubSlotIds.some(id => claimed.includes(id))) return undefined;

  const candidates: number[] = [];
  for (let i = 0; i < workoutSubSlotIds.length; i++) {
    if (i === storedPlanReference) continue;
    const subSlotIds = workoutSubSlotIds[i];
    if (subSlotIds && workedSubSlotIds.every(id => subSlotIds.includes(id))) {
      candidates.push(i);
    }
  }

  // two workouts that both hold every logged exercise cannot tell us which one it was
  return candidates.length === 1 ? candidates[0] : undefined;
}

/**
 * The sub-slots a workout's own exercise list shows a FINISHED set on (#5640).
 *
 * This is the evidence `decidePlanReferenceHeal` argues from, so it reads defensively: a
 * document may have been written by an older version of the app, or be damaged, and a hole in
 * the list or a set with no state must not throw or count. Only a `Done` set counts — a set
 * that was merely opened, prefilled or skipped says nothing about which workout this was.
 */
export function subSlotIdsWithAFinishedSet(
  exercises: readonly { subSlotId?: string; sets?: readonly ({ state?: LogRecordState } | undefined)[] }[] | undefined,
): string[] {
  const found = new Set<string>();
  for (const exercise of exercises ?? []) {
    if (exercise?.subSlotId && exercise.sets?.some(set => set?.state === LogRecordState.Done)) {
      found.add(exercise.subSlotId);
    }
  }
  return [...found];
}

/**
 * The earliest time, as stored (UTC ISO), at which any set of this workout was finished, or
 * undefined when no finished set carries a time (df-532ecfe078a4). Sets logged by older app
 * versions may have no time, so a missing or non-string one is skipped rather than trusted.
 */
export function earliestFinishedSetTimeUtc(
  exercises: readonly { sets?: readonly ({ state?: LogRecordState; completedTime_utc?: unknown } | undefined)[] }[] | undefined,
): string | undefined {
  let earliest: string | undefined;
  for (const exercise of exercises ?? []) {
    for (const set of exercise?.sets ?? []) {
      if (set?.state !== LogRecordState.Done) continue;
      const time = set.completedTime_utc;
      if (typeof time !== 'string' || time === '') continue;
      // both sides are written by luxon's toUTC().toISO(), so the strings sort as times do
      if (earliest === undefined || time < earliest) earliest = time;
    }
  }
  return earliest;
}

/**
 * Whether a plan-reference repair is explained by a program change made after the sets were
 * logged, rather than by the #5640 bad write (df-532ecfe078a4).
 *
 * `backupWorkoutSubSlotIds` is the plan as journalled by the first backup taken after the
 * workout's earliest finished set — that is, the plan the sets were logged under, or a later
 * one when nothing changed it. If its workout at the STORED reference holds every logged
 * sub-slot, the stored label was right for the plan the user trained under, and the repair
 * only follows the plan to its new shape. A backup taken with no plan change (a weekly
 * check-in, say) holds the current plan, which the repair has just found does not hold them,
 * so a genuine mis-stamp still answers false.
 *
 * @param storedPlanReference the plan reference written on the document before the repair
 * @param workedSubSlotIds the sub-slots the user has logged at least one finished set on
 * @param backupWorkoutSubSlotIds each backed-up plan workout's sub-slot ids, in plan order
 */
export function programChangeExplainsPlanReferenceHeal(
  storedPlanReference: number,
  workedSubSlotIds: readonly string[],
  backupWorkoutSubSlotIds: readonly (readonly string[] | undefined)[],
): boolean {
  if (workedSubSlotIds.length === 0) return false;
  const claimed = backupWorkoutSubSlotIds[storedPlanReference];
  if (!Array.isArray(claimed)) return false;
  return workedSubSlotIds.every(id => claimed.includes(id));
}
